/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import AudioManager from '../../../Audio/AudioManager';
import Game from '../../../Game/Game';
import GameSetup from '../../../Game/GameSetup';
import ClientEvents from '../../../Game/Integrations/ClientEvents';
import RecallManager from '../../../Game/RecallManager';
import RecoveryManager from '../../../Game/RecoveryManager';
import WinCelebrationManager from '../../../Game/WinCelebrationManager';
import LocalizationComponent from '../../../Localization/LocalizationComponent';
import PaylineManager from '../../../Paylines/PaylineManager';
import PaylinesDefinitionComponent from '../../../Paylines/PaylinesDefinitionComponent';
import { AutoPlayManager } from '../../../Playbar/AutoPlayManager';
import Bangup from '../../../Playbar/Bangup';
import BetManager from '../../../Playbar/BetManager';
import PlaybarManager from '../../../Playbar/PlaybarManager';
import UserMessageManager from '../../../Playbar/UserMessageManager';
import WinMeter from '../../../Playbar/WinMeter';
import QuickStopComponent from '../../../Reel/QuickStop/QuickStopComponent';
import ReelManager from '../../../Reel/ReelManager';
import Communicator from '../../../Server/Communicator';
import PlayResultComponent from '../../../Server/PlayResultComponent';
import SplashScreenComponent from '../../../StartUp/SplashScreenComponent';
import StateManager from '../../../StateManager/StateManager';
import SymbolSet from '../../../Symbol/SymbolSet';
import ClassUtil from '../../ClassUtil';
import ReferencedComponent from '../ComponentReference/ReferencedComponent';
import ConfiguratorBaseComponent from '../Configurator/ConfiguratorBaseComponent';
import DemoManager from '../DemoManager';
import UpdateableLabelComponent from '../Observable/UpdateableLabelComponent';
import PopupManager from '../Popup/PopupManager';
import PopupModal from '../Popup/PopupModal';
import MutatorComponentBase from './MutatorComponentBase';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

interface IComponentCopyData
{
    ComponentName: string;
    Copies?: number;
    InstanceID?: string;
}

@cc._decorator.ccclass('ComponentCopyData')
class ComponentCopyData implements IComponentCopyData
{
    @property()
    public ComponentName: string = '';
    @property()
    public Copies?: number = 1;
    @property()
    public InstanceID?: string = '';

    public toString(): string
    {
        return ComponentCopyData.GetComponentKey(this.ComponentName, this.InstanceID);
    }

    public static GetComponentKey(componentName: string, instanceID: string): string
    {
        return `${componentName}${instanceID ? `-${instanceID}` : ''}`;
    }
}

@cc._decorator.ccclass
@cc._decorator.executeInEditMode
export default class RequiredComponentsMutator extends MutatorComponentBase
{
    public static readonly InstanceIdKey: string = 'InstanceId';
    public static readonly ObservableKey: string = 'ObservableInstanceID';

    @property({ displayName: 'Display Key', override: true })
    protected shouldModify: boolean = false;

    @property()
    public get PerformCheck(): boolean { return false; }
    public set PerformCheck(value: boolean) { this.execute(); }

    @property({ type: [ComponentCopyData] })
    protected copyDatas: ComponentCopyData[] = [];

    private _componentMap: Map<string, cc.Component[]> = null;

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this.loadDefaults();
    }

    public onRestore(): void
    {
        this.loadDefaults();
    }

    protected mapNodeHandler(componentNames: string[], target: cc.Node): boolean
    {
        for (let componentName of componentNames)
        {
            let components: cc.Component[] = target.getComponents(componentName);

            components.forEach(component =>
                {
                    let instanceId: string = component[RequiredComponentsMutator.InstanceIdKey]
                        || component[RequiredComponentsMutator.ObservableKey];
                    let key: string = ComponentCopyData.GetComponentKey(componentName, instanceId);
                    if (this.shouldModify) { this.log(this.node, key); }

                    let foundComponents: cc.Component[] = this._componentMap.get(key);
                    if (!foundComponents)
                    {
                        foundComponents = [component];
                        this._componentMap.set(key, [component]);
                        return;
                    }
                    if (foundComponents.indexOf(component) < 0)
                    {
                        foundComponents.push(component);
                    }
                });
        }
        return true;
    }

    protected execute(): void
    {
        let uniqueFilter: (value: ComponentCopyData, index: number, self: ComponentCopyData[]) => Boolean
            = (value: ComponentCopyData, index: number, self: ComponentCopyData[]) =>
        {
            return self.indexOf(value) === index;
        };

        let uniqueComponents: string[]
            = this.copyDatas.filter(uniqueFilter).map(copyData => copyData.ComponentName);

        this._componentMap = new Map();

        let shouldModify: boolean = this.shouldModify;
        for (let child of cc.director.getScene().children)
        {
            super.execute(child, [this.mapNodeHandler.bind(this, uniqueComponents)], `Mapping ${child.name}.`);
            this.shouldModify = shouldModify;
        }

        for (let copyData of this.copyDatas)
        {
            let foundComponents: cc.Component[] = this._componentMap.get(copyData.toString());
            this.logCountError(copyData, foundComponents ? foundComponents.length : 0);
        }
    }

    private logCountError(copyData: ComponentCopyData, count: number): void
    {
        if (count === 0)
        {
            this.error(this.node, `No instances found for: [${copyData}]`);
        }
        else if (count !== copyData.Copies)
        {
            this.warn(this.node,
                `Mismatch: [${copyData}] - Copies expected: [${copyData.Copies}] actual: [${count}]`);
        }
        else
        {
            this.log(this.node, `Success: Found (${count}) ${copyData}`);
        }
    }

    // tslint:disable:max-line-length
    private loadDefaults(): void
    {
        if (!CC_EDITOR || this.copyDatas.length > 0) { return; }

        let copyDatas: IComponentCopyData[] =
        [
            { ComponentName: ClassUtil.getName(ConfiguratorBaseComponent) },
            { ComponentName: ClassUtil.getName(LocalizationComponent) },
            { ComponentName: ClassUtil.getName(Game) },
            { ComponentName: ClassUtil.getName(GameSetup) },
            { ComponentName: ClassUtil.getName(Communicator) },
            { ComponentName: ClassUtil.getName(StateManager), Copies: 1 },
            { ComponentName: ClassUtil.getName(ReelManager), Copies: 1 },
            { ComponentName: ClassUtil.getName(QuickStopComponent), Copies: 1 },
            { ComponentName: ClassUtil.getName(SymbolSet), Copies: 1 },
            { ComponentName: ClassUtil.getName(BetManager) },
            { ComponentName: ClassUtil.getName(WinMeter) },
            { ComponentName: ClassUtil.getName(Bangup) },
            { ComponentName: ClassUtil.getName(PlaybarManager) },
            { ComponentName: ClassUtil.getName(WinCelebrationManager) },
            { ComponentName: ClassUtil.getName(DemoManager) },
            { ComponentName: ClassUtil.getName(PopupManager) },
            { ComponentName: ClassUtil.getName(PopupModal) },
            { ComponentName: ClassUtil.getName(AutoPlayManager) },
            { ComponentName: ClassUtil.getName(AudioManager) },
            { ComponentName: ClassUtil.getName(ClientEvents) },
            { ComponentName: ClassUtil.getName(SplashScreenComponent) },
            { ComponentName: ClassUtil.getName(PlayResultComponent) },
            { ComponentName: ClassUtil.getName(UserMessageManager) },
            { ComponentName: ClassUtil.getName(RecoveryManager) },
            { ComponentName: ClassUtil.getName(PaylineManager) },
            { ComponentName: ClassUtil.getName(PaylinesDefinitionComponent), InstanceID: 'Payline.Definition' },
            { ComponentName: ClassUtil.getName(RecallManager) },
            { ComponentName: ClassUtil.getName(ReferencedComponent), InstanceID: 'Recall.Cover' },
            { ComponentName: ClassUtil.getName(ReferencedComponent), InstanceID: 'Paytable' },
            { ComponentName: ClassUtil.getName(UpdateableLabelComponent), InstanceID: 'Game.DisplayName', Copies: 2 },
            { ComponentName: ClassUtil.getName(UpdateableLabelComponent), InstanceID: 'ServerTime.String' },
            { ComponentName: ClassUtil.getName(UpdateableLabelComponent), InstanceID: 'SessionTime.String' }
        ];

        this.copyDatas = [];
        for (let copyData of copyDatas)
        {
            this.copyDatas.push(Object.assign(new ComponentCopyData(), copyData));
        }
    }
}
